import axiosInstance from './axiosInstance';

export type AuditFrequency = 'yearly' | 'half_yearly' | 'quarterly';

export interface AuditTypeRef {
  _id: string;
  name: string;
}

export interface CreateAmsProjectAudit {
  auditTypeId: string;
  auditFrequency: AuditFrequency;
  auditors: string[]; // Array of user IDs
  scope: number;
  auditStartDate?: string; // YYYY-MM-DD
}

export interface BulkCreateAmsProjectAuditRequest {
  amsProjectId: string;
  audits: CreateAmsProjectAudit[];
}

export interface AmsProjectAudit {
  _id: string;
  amsProjectId: string;
  auditTypeId: AuditTypeRef | string;
  auditFrequency: AuditFrequency;
  auditStartDate?: string;
  auditors: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
  scope: number;
  status: 'active' | 'inactive';
  /** Field-run state. 'done' = submitted → the audit is history: locked, read-only. */
  runStatus?: 'inprog' | 'done' | 'hold';
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  updatedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BulkCreateAmsProjectAuditResponse {
  created: AmsProjectAudit[];
  failed: Array<{
    audit: CreateAmsProjectAudit;
    error: string;
  }>;
}

export interface UpdateAmsProjectAudit {
  auditTypeId?: string;
  auditFrequency?: AuditFrequency;
  auditors?: string[];
  scope?: number;
  auditStartDate?: string;
  status?: 'active' | 'inactive';
}

const amsProjectAuditService = {
  /**
   * Create multiple audits for an AMS project
   */
  createBulkAudits: async (data: BulkCreateAmsProjectAuditRequest): Promise<BulkCreateAmsProjectAuditResponse> => {
    const response = await axiosInstance.post('/ams/ams-project-audits/bulk', data);
    return response.data.data;
  },

  /**
   * Get all audits for an AMS project
   */
  getProjectAudits: async (amsProjectId: string): Promise<AmsProjectAudit[]> => {
    const response = await axiosInstance.get('/ams/ams-project-audits', {
      params: { amsProjectId },
    });
    return response.data.data;
  },

  /**
   * Get audit by ID
   */
  getAuditById: async (auditId: string): Promise<AmsProjectAudit> => {
    const response = await axiosInstance.get(`/ams/ams-project-audits/${auditId}`);
    return response.data.data;
  },

  /**
   * Update audit
   */
  updateAudit: async (auditId: string, data: UpdateAmsProjectAudit): Promise<AmsProjectAudit> => {
    const response = await axiosInstance.patch(`/ams/ams-project-audits/${auditId}`, data);
    return response.data.data;
  },

  /**
   * Delete audit
   */
  deleteAudit: async (auditId: string): Promise<void> => {
    await axiosInstance.delete(`/ams/ams-project-audits/${auditId}`);
  },
};

export default amsProjectAuditService;
